<template>
    <div class="kleptos-window">

        <!-- TITLEBAR -->
        <div class="kleptos-window-titlebar">

            <!-- TEXT -->
            <span class="text">
                {{ gameObject.name }}
            </span>

            <!-- TITLEBAR BUTTONS -->
            <div class="kleptos-window-titlebar-btns">

                <!-- MINIMIZE BTN -->
                <button
                    v-if="gameObject.windowData.minimizable"
                    class="kleptos-window-titlebar-btn btn-minimize"
                    @click="minimizeWindow({ target: gameObject, bool: true })"
                >
                    &minus;
                </button>

                <!-- MAXIMIZE BTN -->
                <button
                    v-if="gameObject.windowData.maximizable"
                    class="kleptos-window-titlebar-btn btn-maximize"
                    @click="maximizeWindow(gameObject)"
                >
                    <i v-show="!gameObject.windowData.maximized" class="fa fa-window-maximize"></i>
                    <i v-show="gameObject.windowData.maximized" class="fa fa-window-restore"></i>
                </button>

                <!-- CLOSE BTN -->
                <button
                    class="kleptos-window-titlebar-btn btn-close"
                    @click="closeWindow(gameObject)"
                >
                    &times;
                </button>

            </div>
        </div>

        <!-- BODY  -->
        <div class="kleptos-window-body">

            <!-- DYNAMIC COMPONENT -->
            <component
                :is="component"
                v-if="component"
                :node="gameObject"
            />

        </div>
    </div>
</template>

<script>
    import { mapActions } from 'vuex';

    // cache FileSystem, it's a system program and we need it right away
    import '@/components/KleptOS/Programs/FileSystem/FileSystem';

    export default {
        props: {
            gameObject: {
                type: Object,
                required: true
            }
        },
        data() {
            return {
                component: null
            }
        },
        computed: {
            //--------------------------------------------------------------------
            // ASYNC COMPONENTS
            // https://medium.com/scrumpy/dynamic-component-templates-with-vue-js-d9236ab183bb
            // https://vueschool.io/articles/vuejs-tutorials/async-vuejs-components/
            // TODO: show splash screen during load
            //--------------------------------------------------------------------

            loader() {
                if (!this.gameObject) {
                    return null;
                }
                return () => import(`@/components/KleptOS${this.gameObject.component}`); // Eg. ../Programs/FileSystem/FileSystem
            }
        },

        mounted() {
            this.loader()
                .then(() => {
                    this.component = () => this.loader()
                })
                .catch(() => {
                    console.warn('Oops! Component failed to load.')
                    // this.component = () => import('../Programs/FileSystem/FileSystem');
                })
        },
        methods: {
            ...mapActions('gameOS', [
                'closeWindow',
                'maximizeWindow',
                'minimizeWindow'
            ]),
            // closeWindowAndReport() {
            //     console.log('** Close triggered **');
            //     console.log(this.gameObject);
            //     this.closeWindow(this.gameObject);
            // }
        }
    }
</script>

<style lang="scss" scoped>

    //--------------------------------------------------------------------
    // WINDOW
    //--------------------------------------------------------------------

    .kleptos-window {
        display: flex;
        flex-direction: column;
        height: 100%;
        color: rgba(white, 0.4);
        // overflow: hidden; // need to review this
        box-shadow: 0 3px 14px 2px rgba(black, 0.2);
        pointer-events: auto;

        // CORNER DRAG SYMBOL
        &:after {
            content: "";
            position: absolute;
            bottom: 2px;
            right: 2px;
            width: 0;
            height: 0;
            border-style: solid;
            border-width: 0 0 12px 12px;
        }
    }

    // active
    .theme-95 .active .kleptos-window {
        box-shadow: 0 3px 18px 5px rgba(black, 0.2);
    }

    // maximized parent (override)
    .theme-95 .is-maximized .kleptos-window {
        box-shadow: none;
        box-shadow: inset 0 -2px 10px 2px rgba(black, 0.04); // subtle shadow over the taskbar
    }

    .theme-95 .kleptos-window {
        background: #c7c6b7;
        &:after { border-color: transparent transparent darken(#c7c6b7, 10%) transparent; }
        &:after:hover { border-color: transparent transparent #809ab8 transparent; }
    }

    .theme-os .kleptos-window {
        box-shadow: 0 3px 18px 5px rgba(black, 0.4); // used to be on panel wrapper - if issue
        &:after { border-color: transparent transparent rgba(white, 0.1) transparent; }
        &:after:hover { border-color: transparent transparent #809ab8 transparent; }
    }

    //--------------------------------------------------------------------
    // WINDOW TITLEBAR
    //--------------------------------------------------------------------

    .kleptos-window-titlebar {
        position: relative; // [LOCKED]
        display: flex;
        align-items: center;
        justify-content: center;
        height: rem-calc(34);
        min-height: rem-calc(34); // [LOCKED] - stops glitch when resizing
        // transition: background ease 100ms;
    }

    .kleptos-window-titlebar > .text {
        pointer-events: none; // for invoking double click 'maximize'
        // min-width: rem-calc(100); // stops weird Safari glitch
    }

    .theme-95 .kleptos-window-titlebar {
        background: lighten(#b3b2a5, 3%);
        font-size: rem-calc(15);
        font-weight: 500;
        // color: #333;
        color: darken(#bab9ad, 25%);
    }

    // active
    .theme-95 .active .kleptos-window-titlebar {
        background: darken(#b3b2a5, 5%);
        color: black;
    }

    .theme-os .kleptos-window-titlebar {
        // background: rgba(black, 0.43);
        background: rgba(black, 0.8);
        font-size: rem-calc(13);
        color: rgba(white, 0.5);
    }

    // .theme-95 .dragging .kleptos-window-titlebar {
        // background: darken(#b3b2a5, 8%);
    // }

    .theme-os .dragging .kleptos-window-titlebar {
        background: rgba(black, 0.7);
    }

    //--------------------------------------------------------------------
    // TITLEBAR BUTTONS
    //--------------------------------------------------------------------

    .kleptos-window-titlebar-btns {
        display: flex;
        position: absolute;
        top: 0;
        right: 0;
        height: 100%;
    }

    .btn-close,
    .btn-minimize,
    .btn-maximize {
        width: rem-calc(40);
        min-width: rem-calc(40);
        border: 0;
        border-left: 1px solid transparent;
        font-size: rem-calc(35);
        text-align: center;
        line-height: 0;
        transition: all ease 150ms;
        background: none;
        overflow: hidden;

        i {
            pointer-events: none; // [LOCKED] - for event.target.class of targeting button without children
        }
    }

    .btn-maximize {
        font-size: rem-calc(14);
    }

    .theme-95 .btn-close,
    .theme-95 .btn-minimize,
    .theme-95 .btn-maximize {
        color: rgba(white, 0.4);

        &:hover {
            background: rgba(black, 0.07);
        }
    }

    .theme-os .btn-close,
    .theme-os .btn-minimize,
    .theme-os .btn-maximize {
        color: rgba(white, 0.2);

        &:hover {
            background: rgba(black, 0.07);
        }
    }

    //--------------------------------------------------------------------
    // WINDOW BODY
    //--------------------------------------------------------------------

    .kleptos-window-body {
        display: flex;
        // position: relative; // is this a good move?
        // border: 1px dashed black;
        height: 100%;
        overflow: hidden;
        padding: rem-calc(30 10); // get global
    }

    .theme-95 .kleptos-window-body {
        color: #333; // todo: var?;
    }

    .theme-os .kleptos-window-body {
        background: rgba(#1e1e1e, 1);
    }

    //--------------------------------------------------------------------
    // TODO: make global window styles
    //--------------------------------------------------------------------

    // Make this global
    // .kleptos-window-body /deep/ ul {
    //     list-style: none;
    //     padding: 0;
    //     font-size: 20px;

    //     a {
    //         // color: white;
    //         // text-transform: none;
    //     }
    // }
</style>